<template>
  <div class="actions-bar" :style="{ width: width }">
    <UndoRedo @onUndo="undo" @onRedo="redo" />

    <div class="right-actions">
      <BbBtn class="preview-button" size="small" type="secondary" @click="onTranslateEmail">
        <BbIcon size="small" name="translate" />
      </BbBtn>
      <BbBtn class="preview-button" size="small" type="secondary" @click="onOpenReferencesList">
        <BbIcon size="small" name="bulletlist" />
        <TextBody i18n-key="author.references" />
      </BbBtn>
      <BbBtn class="preview-button" size="small" type="secondary" @click="onPreview">
        <TextBody i18n-key="common.preview" />
      </BbBtn>

      <PublishConnectorButton v-if="hasAnyConnectorAccess" :connector-access="connectorAccess" @download="onDownload" />

      <BbTooltip v-bind="{
        placement: 'top',
        offset: 4,
        isDisabled: false,
        content: isDownloadInProgress ? t('common.processing') : t('common.download'),
      }">
        <BbBtn v-show="!hasAnyConnectorAccess" size="small"
          :icon="isDownloadInProgress ? 'spin' : 'download'" icon-size="small" is-icon-button
          :is-disabled="isDownloadInProgress" @click="onDownload" />
      </BbTooltip>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import { IsDownloadInProgress, GetUserData } from '~/constants/getters';
import { TRANSLATION_TYPE } from '~/constants/documents';
import { CONNECTOR_BINDINGS } from '~/constants/user';
import { hasAnyRole } from '~/utils/roles';

import TextBody from '~/components/common/TextBody.vue';
import UndoRedo from '~/components/conversation/authoring/UndoRedo.vue';
import useDocumentEditor from '~/composables/useDocumentEditor';
import useDocuments from '~/composables/useDocuments';
import useModals from '~/composables/useModals';
import PublishConnectorButton from '~/components/conversation/authoring/PublishConnectorButton.vue';

export default {
  components: { TextBody, UndoRedo, PublishConnectorButton },
  props: {
    width: {
      type: String,
      default: '100%',
    },
  },
  emits: ['onUndo', 'onRedo'],
  setup(props, { emit }) {
    const route = useRoute();
    const store = useStore();
    const { t } = useI18n();

    const { downloadAuthoredDocument } = useDocuments();
    const { showPreviewAuthorDocument, setReferencesListOpened, showTranslationModal } = useModals();
    const { saveHtmlDocumentContentToDb } = useDocumentEditor();

    const isDownloadInProgress = computed(() =>
      store.getters[`documentStore/${IsDownloadInProgress}`](route.params.conversationId),
    );

    const hasAccess = (binding) => {
      // Connector buttons gate on org-feature availability, not RBAC permission.
      // The `feature:<resource>` role is derived server-side only when the org feature
      // is enabled (see server claims.ts), so it is immune to admin/is_beta_user
      // Casbin wildcards that would otherwise force the permission map to true.
      const roles = store.getters[`authStore/${GetUserData}`]?.roles || [];
      return hasAnyRole(roles, `feature:${binding.resource}`);
    };
    const connectorAccess = computed(() =>
      Object.fromEntries(Object.entries(CONNECTOR_BINDINGS).map(([key, binding]) => [key, hasAccess(binding)])),
    );
    const hasAnyConnectorAccess = computed(() => Object.values(connectorAccess.value).some(Boolean));

    const onOpenReferencesList = async () => {
      setReferencesListOpened({ open: true, editable: true });
    };

    const undo = () => {
      emit('onUndo');
    };

    const redo = () => {
      emit('onRedo');
    };

    const onTranslateEmail = async () => {
      await saveHtmlDocumentContentToDb();
      showTranslationModal(`email/${route.params.conversationId}/content.mjml`, 'Email', TRANSLATION_TYPE.email);
    };

    const onDownload = async () => {
      await saveHtmlDocumentContentToDb();
      await downloadAuthoredDocument(route.params.conversationId);
    };

    const onPreview = async () => {
      await saveHtmlDocumentContentToDb();
      showPreviewAuthorDocument();
    };

    return {
      undo,
      redo,
      onDownload,
      t,
      onPreview,
      isDownloadInProgress,
      connectorAccess,
      hasAnyConnectorAccess,
      onOpenReferencesList,
      onTranslateEmail,
    };
  },
};
</script>

<style lang="scss" scoped>
.actions-bar {
  position: sticky;
  top: 0;
  overflow: hidden;
  background: $bb-neutral-0;
  margin: auto;
  display: flex;
  justify-content: space-between;
  height: 48px;
  box-shadow: 0px 5px 8px 0px rgba(0, 0, 0, 0.1);
  border-radius: 8px 8px 0px 0px;
  border: 1px solid $bb-neutral-4;
  flex-shrink: 0;
  padding: 6px $bb-spacing-3;

  .right-actions {
    display: flex;
    gap: $bb-spacing-2;

    .preview-button {
      :deep(.v-btn__content) {
        gap: $bb-spacing-1;
      }
    }

    .bb-btn.primary {
      height: 36px;
      border-radius: 100px;
      background: $bb-neutral-0 !important;
      color: #1c1c1d !important;
      border: 1px solid $bb-neutral-4;
      cursor: pointer;

      &:hover {
        background: $bb-neutral-0 !important;
      }
    }

    :deep(.bb-btn.v-btn--disabled) {
      background: $bb-neutral-3 !important;
    }

    :deep(.bb-btn.primary.dropdown-button) {
      background-color: $bb-neutral-6 !important;
      border: unset !important;
      padding: 0;
    }
  }
}
</style>
